// Void an order's surplus jobs (jobs whose parts are no longer needed once every line has shipped).
//
// Moved from the Shipping page's Build Shipment tab (pages/ShippingQueuePage.jsx handleVoidSurplus, its only entry
// point; the tab goes away in the Phase 1 shipping redesign, 2026-10-27). Mike: "Keep Void surplus as an SO-page
// office tool". The questions and messages are the old ones word for word.
//
// `ui` = features/desktopUi shape (alert, confirm, pin). Resolves {done, voided?, message?}.
import config from '../../config.js';
import { detailToString } from '../../utils/apiError';

const API_BASE = config.API_BASE_URL;

/** GET /sales_orders/{id}/surplus_jobs → the jobs array (throws on a refusal). */
export async function loadSurplusJobs(salesOrderId) {
  const res = await fetch(`${API_BASE}/sales_orders/${salesOrderId}/surplus_jobs`);
  if (!res.ok) throw new Error('Failed to fetch surplus jobs');
  const data = await res.json();
  return (data && data.jobs) || [];
}

/** True when every line has shipped at least what was ordered (the old banner's rule). No lines = false. */
export function allLinesShipped(lines) {
  const list = Array.isArray(lines) ? lines : [];
  return list.length > 0 && list.every((l) => (l.qty_shipped || 0) >= (l.quantity_ordered || 0));
}

export function voidSurplusQuestion(jobs) {
  const jobList = jobs.map((j) =>
    `  ${j.master_job_num} — ${j.status} (${j.task_count} task${j.task_count !== 1 ? 's' : ''})`,
  ).join('\n');
  return `Void ${jobs.length} surplus job${jobs.length !== 1 ? 's' : ''}?\n\n`
    + `These jobs have unshipped parts that are no longer needed:\n\n${jobList}\n\n`
    + 'This will set all remaining tasks to "Voided" status.';
}

/** List the surplus jobs, confirm, PIN, POST /sales_orders/{id}/void_surplus. */
export async function voidSurplusJobs({ salesOrderId, ui }) {
  if (!salesOrderId) return { done: false };
  let jobs;
  try {
    jobs = await loadSurplusJobs(salesOrderId);
  } catch (err) {
    const message = `Error fetching surplus jobs: ${err.message}`;
    ui.alert(message);
    return { done: false, message };
  }
  if (jobs.length === 0) {
    const message = 'No surplus jobs found for this sales order.';
    ui.alert(message);
    return { done: false, message };
  }
  if (!(await ui.confirm(voidSurplusQuestion(jobs)))) return { done: false };
  const raw = await ui.pin('Enter your 4-digit PIN:');
  const pin = raw ? String(raw).trim() : null;
  if (!pin) return { done: false };
  try {
    const res = await fetch(`${API_BASE}/sales_orders/${salesOrderId}/void_surplus`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pin, reason: 'Surplus - sales order fully shipped' }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(detailToString(err.detail) || 'Error voiding surplus jobs');
    }
    const result = await res.json();
    const n = result.voided_count || jobs.length;
    const message = `${n} surplus job${n !== 1 ? 's' : ''} voided successfully.`;
    ui.alert(message);
    return { done: true, voided: n, message };
  } catch (err) {
    const message = `Error voiding surplus jobs: ${err.message}`;
    ui.alert(message);
    return { done: false, message };
  }
}
